import { Component, signal, inject, computed } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  of,
  pairwise,
  startWith,
  switchMap,
  tap,
} from 'rxjs';
import { IdTagStatusEnum } from 'src/app/shared/enums/id-tag-status.enum';
import { IdTagEnrichRequest, IdTagService } from 'src/app/shared/services/id-tag.service';
import { IdTag, IdTagSearchEvent, LookupIdTag } from '../id-tag-model';
import { NumberOnlyHelper } from '../../../shared/helpers/number-only';
import { PageEvent } from '@angular/material/paginator';
import { SnackbarService } from 'ok-angular-shared/components';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { AccessGroup } from '../../access-groups/access-group.model';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorModule } from '@angular/material/paginator';
import { EnvironmentLoaderService } from 'src/app/shared/services/environment-loader.service';
import { RequiresFeatureRoleDirective } from 'src/app/shared/directives/requires-feature-role.directive';
import { AdmG4AgreementNumberComponent } from 'src/app/shared/components/adm-g4-agreement-number/adm-g4-agreement-number.component';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { IdTagTypeEnum } from 'src/app/shared/enums/id-tag-type.enum';

export interface IdTagProcessModalData {
  idTagId: number;
}

interface IdTagFormData {
  enableIdTag: FormControl<boolean>;
  selectedPrintedId: FormControl<number | null>;
  printedId: FormControl<number | null>;
  idToken: FormControl<string | null>;
  selectedAccessGroups: FormControl<string[]>;
  agreementNumber: FormControl<number | null>;
}

@Component({
  selector: 'adm-id-tag-process-modal',
  templateUrl: './id-tag-process-modal.component.html',
  styleUrls: ['./id-tag-process-modal.component.scss'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatAutocompleteModule,
    MatIconModule,
    MatButtonModule,
    MatCheckboxModule,
    MatPaginatorModule,
    CustomDropDownArrowDirective,
    RequiresFeatureRoleDirective,
    AdmG4AgreementNumberComponent,
  ],
})
export class IdTagProcessModalComponent {
  dialogRef = inject<MatDialogRef<IdTagProcessModalComponent>>(MatDialogRef);
  data = inject<IdTagProcessModalData>(MAT_DIALOG_DATA);
  private idTagService = inject(IdTagService);
  private snackbarService = inject(SnackbarService);
  private accessGroupService = inject(AccessGroupService);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  public roles = this.environmentLoaderService.virtualRoles;
  saving = signal(false);
  public idTag = signal<IdTag | null>(null);
  public filteredLookupIdTags: LookupIdTag[];
  public printedIdAlreadyUsed = false;
  public foundNoPrintedIds: string | null = null;
  accessGroups: AccessGroup[] = [];
  private idTagIdSubject = new BehaviorSubject<number | null>(this.data?.idTagId);
  IdTagTypeEnum = IdTagTypeEnum;

  constructor() {
    if (!this.data || this.data.idTagId === undefined || this.data.idTagId === null) {
      throw new Error('IdTagProcessModal requires data with idTagId to be provided');
    }
    this.idTagIdSubject
      .pipe(
        switchMap(idTagId => {
          if (idTagId) {
            return this.idTagService.getIdTag(idTagId);
          }
          return of(null);
        }),
        map(idTag => {
          if (idTag) {
            this.idTag.set(idTag);
            this.form.patchValue({
              printedId: idTag.printedId,
              idToken: idTag.idToken,
              enableIdTag: idTag.status.toString() === IdTagStatusEnum[IdTagStatusEnum.Active],
              agreementNumber: this.isFleetIdTag(idTag) ? idTag.paymentInfo : null,
            });
            if (!this.isFleetIdTag(idTag)) {
              this.form.controls.agreementNumber.disable();
            }

            if (
              idTag.status.toString() === IdTagStatusEnum[IdTagStatusEnum.Active] ||
              idTag.status.toString() === IdTagStatusEnum[IdTagStatusEnum.NoIdTagGroup] ||
              idTag.status.toString() === IdTagStatusEnum[IdTagStatusEnum.Blocked]
            ) {
              this.form.controls.enableIdTag.enable();
            } else {
              this.form.controls.enableIdTag.disable();
            }
          }

          return idTag;
        }),
        switchMap(idTag => {
          if (idTag) {
            return this.accessGroupService
              .getAccessGroups({
                paging: {
                  pageIndex: 0,
                  pageSize: 100,
                  length: 0,
                },
                sorting: null,
                query: null,
                organizationId: idTag.organizationId,
                carType: AccessGroupCarType.CompanyCar,
              })
              .pipe(
                tap(ag => {
                  if (ag.items.length > 0) {
                    this.form.controls.selectedAccessGroups.enable();
                  }
                  this.accessGroups = ag.items;
                })
              );
          }

          return of(null);
        })
      )
      .subscribe();

    this.form.controls.printedId.valueChanges
      .pipe(
        startWith(this.form.controls.printedId.value),
        debounceTime(300),
        distinctUntilChanged(),
        pairwise(),
        filter(([prev, curr]) => curr !== null && NumberOnlyHelper.isNumber(curr) && curr.toString() !== prev?.toString()),
        switchMap(([prev, curr]) => {
          if (curr === null) {
            return of([] as LookupIdTag[]);
          }
          this.printedIdAlreadyUsed = false;
          this.foundNoPrintedIds = null;
          return this.idTagService.lookupIdTags(curr).pipe(
            tap(filteredLookupIdTags => {
              this.filteredLookupIdTags = filteredLookupIdTags;
              if (this.filteredLookupIdTags.length === 0) {
                this.form.controls.printedId.setErrors({ foundNoPrintedIds: true });
                this.foundNoPrintedIds = `Der blev ikke fundet nogen brik ID'er`;
              }
            })
          );
        }),
        takeUntilDestroyed()
      )
      .subscribe();
  }

  public isFleetIdTag(idTag: IdTag | null | undefined): boolean {
    if (!idTag) {
      return false;
    }

    return idTag.idTagType?.toString() === IdTagTypeEnum[IdTagTypeEnum.FleetIdTag];
  }

  public form: FormGroup<IdTagFormData> = new FormGroup<IdTagFormData>({
    enableIdTag: new FormControl<boolean>(false, { nonNullable: true }),
    selectedPrintedId: new FormControl<number | null>(null, [Validators.required]),
    printedId: new FormControl<number | null>(null, [
      Validators.required,
      Validators.pattern('^[0-9]*$'),
      Validators.minLength(1),
      Validators.maxLength(6),
    ]),
    idToken: new FormControl<string | null>({ value: null, disabled: true }, [Validators.required]),
    selectedAccessGroups: new FormControl<string[]>([], { nonNullable: true }),
    agreementNumber: new FormControl<number | null>({ value: null, disabled: false }),
  });

  readonly formInvalid = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map(status => status !== 'VALID')
    ),
    { initialValue: true }
  );

  readonly saveDisabled = computed(() => this.formInvalid() || this.saving());

  compareObjects(ag1: AccessGroup, ag2: AccessGroup): boolean {
    return ag1?.identifier === ag2?.identifier;
  }

  getFirstAccessGroupName() {
    const selectedAccessGroups = this.form.controls.selectedAccessGroups.value;
    return selectedAccessGroups.length === 0 ? '' : this.accessGroups.find(ag => ag.identifier === selectedAccessGroups[0])?.name;
  }

  public async selectIdTag(value: number | null) {
    if (value) {
      const idTagSearchEvent: IdTagSearchEvent = new IdTagSearchEvent(new PageEvent());
      idTagSearchEvent.printedId = value;
      const existingIdTagsWithPrintedId = await firstValueFrom(this.idTagService.getIdTags(idTagSearchEvent));
      this.printedIdAlreadyUsed = existingIdTagsWithPrintedId.items.length > 0;

      if (this.printedIdAlreadyUsed) {
        this.form.patchValue({ idToken: '' });
        this.form.controls.printedId.setErrors({ printedIdAlreadyUsed: true });
      } else {
        const selectedIdTag = this.filteredLookupIdTags.find(i => i.printedId === value);
        if (!selectedIdTag) {
          return;
        }

        this.form.patchValue({
          selectedPrintedId: selectedIdTag.printedId,
          idToken: selectedIdTag.idToken,
        });
      }
    }
  }

  public closeAndUpdate() {
    this.dialogRef.close(this.idTag);
  }

  public async processIdTagAndClose() {
    if (this.form.invalid) {
      return;
    }
    this.saving.set(true);
    const idTag = this.idTag();
    if (!idTag) {
      this.saving.set(false);
      return;
    }

    const formValue = this.form.getRawValue();

    await firstValueFrom(
      this.idTagService.enrichIdTag(idTag.id, {
        PrintedId: formValue.printedId,
        IdToken: formValue.idToken,
        description: idTag.description,
        accessGroupIdentifiers: formValue.selectedAccessGroups,
        agreementNumber: formValue.agreementNumber,
      } as IdTagEnrichRequest)
    )
      .then(async idTagId => {
        this.dialogRef.close(this.idTag());
      })
      .catch(err => {
        let message = 'Der er sket en ukendt fejl. Brikken blev ikke tildelt det valgte RFID.';
        if (err?.error.code === 20007) {
          message = 'Der sket en fejl i CS management under tildeling af IdTagGroup';
        }
        this.snackbarService.error(`RFID blev ikke gemt. ${message}`);
        console.error(err);
      })
      .finally(() => {
        this.saving.set(false);
      });
  }
}
